'use client';

// 탭 간 동기화를 위한 BroadcastChannel
type AuthSyncMessage = {
	type:
		| 'LOGIN'
		| 'LOGOUT'
		| 'TOKEN_REFRESH'
		| 'ACCOUNT_SWITCH'
		| 'FORCE_LOGOUT'
		| 'TOKEN_REFRESH_REQUEST'
		| 'TOKEN_REFRESH_RESULT'
		| 'TOKEN_REFRESH_FAILED';
	payload?: any;
	timestamp: number;
	tabId: string;
};

class TabSyncManager {
	private channel: BroadcastChannel;
	private tabId: string;
	private listeners: Map<string, ((message: AuthSyncMessage) => void)[]> = new Map();

	constructor() {
		this.tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
		this.channel = new BroadcastChannel('stepin-auth-sync');
		this.setupListener();
	}

	private setupListener() {
		this.channel.addEventListener('message', (event: MessageEvent<AuthSyncMessage>) => {
			const { type, payload, timestamp, tabId } = event.data;

			// 자신이 보낸 메시지는 무시
			if (tabId === this.tabId) return;

			// 너무 오래된 메시지는 무시 (5초 이상)
			if (Date.now() - timestamp > 5000) return;

			console.log(`[TabSync] Received ${type} from ${tabId}`);

			const typeListeners = this.listeners.get(type) || [];
			typeListeners.forEach((listener) => {
				try {
					listener(event.data);
				} catch (error) {
					console.error(`[TabSync] Error in listener for ${type}:`, error);
				}
			});
		});
	}

	// 메시지 브로드캐스트
	broadcast(type: AuthSyncMessage['type'], payload?: any) {
		const message: AuthSyncMessage = {
			type,
			payload,
			timestamp: Date.now(),
			tabId: this.tabId,
		};

		console.log(`[TabSync] Broadcasting ${type}`, payload);
		this.channel.postMessage(message);
	}

	// 리스너 등록
	on(type: AuthSyncMessage['type'], listener: (message: AuthSyncMessage) => void) {
		if (!this.listeners.has(type)) {
			this.listeners.set(type, []);
		}
		this.listeners.get(type)!.push(listener);
	}

	// 리스너 제거
	off(type: AuthSyncMessage['type'], listener: (message: AuthSyncMessage) => void) {
		const typeListeners = this.listeners.get(type);
		if (typeListeners) {
			const index = typeListeners.indexOf(listener);
			if (index > -1) {
				typeListeners.splice(index, 1);
			}
		}
	}

	// 정리
	destroy() {
		this.channel.close();
		this.listeners.clear();
	}

	getTabId() {
		return this.tabId;
	}
}

// 싱글톤 인스턴스
let tabSyncInstance: TabSyncManager | null = null;

export const getTabSync = (): TabSyncManager => {
	if (typeof window === 'undefined') {
		// SSR 환경에서는 더미 객체 반환
		return {
			broadcast: () => {},
			on: () => {},
			off: () => {},
			destroy: () => {},
			getTabId: () => 'ssr',
		} as unknown as TabSyncManager;
	}

	if (!tabSyncInstance) {
		tabSyncInstance = new TabSyncManager();
	}
	return tabSyncInstance;
};

// 페이지 언로드 시 정리
if (typeof window !== 'undefined') {
	window.addEventListener('beforeunload', () => {
		if (tabSyncInstance) {
			tabSyncInstance.destroy();
		}
	});
}
